import { NotificationDefinitionRow } from "services/api/notifications/types";
import { CHANNEL_BADGE_MAP, ENTITY_TYPE_ICONS } from "services/api/notifications/consts";
import { stripHtml, transformDateToLocalDateString } from "helpers";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import { Badge } from "ui/components/Badge";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import Tooltip from "ui/components/Tooltip/Tooltip";
import {
  buildNotificationSchedule,
  formatScheduleDate,
  getExampleExpiryDate,
} from "ui/modules/notifications/schedule";
import { getRepeatDescription } from "./repeatDescription";

export const NOTIFICATION_SEARCH_COLUMNS = (
  t: (key: string) => string,
  handleToggleActive: (id: string, isActive: boolean) => void,
  handleDeleteClick: (event: React.MouseEvent<HTMLSpanElement>, id: string) => void,
): Column[] => [
    {
      title: "",
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => {
        const icon = ENTITY_TYPE_ICONS[row.entityType];
        return <TableIcon icon={icon} />;
      },
    },
    {
      title: t("admin.notification.search.entityType"),
      field: "entityTypeName",
      type: ColumnTypes.string,
      sortable: true,
    },
    {
      title: t("admin.notification.search.message"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => {
        const message = stripHtml(row.messageTemplate);

        // Predložak ide u tooltip — u retku je bio odrezan na pola rečenice, pa se ionako
        // nije dao pročitati. Bez predloška nema što prikazati, pa ostaje samo naslov.
        if (!message) {
          return <div className="max-w-lg text-md">{row.title}</div>;
        }

        return (
          <div className="max-w-lg">
            <Tooltip
              position="top-right"
              size="medium"
              decorate={<span className="text-md">{row.title}</span>}
            >
              <div className="text-sm leading-relaxed whitespace-pre-line">{message}</div>
            </Tooltip>
          </div>
        );
      },
    },

    {
      title: t("admin.notification.search.scope"),
      field: "scopeName",
      type: ColumnTypes.string,
      sortable: true,
    },


    {
      title: t("admin.notification.search.channels"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => (
        <div className="flex gap-1">
          {row.channelNames.map((ch) => {
            const badge = CHANNEL_BADGE_MAP[ch];
            return (
              <Badge key={ch} text={ch} variant={badge?.variant ?? "info"} height="small" showDot />
            );
          })}
        </div>
      ),
    },
    {
      title: t("admin.notification.search.daysBeforeExpiry"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => (
        <span>{row.daysBeforeExpiry != null ? row.daysBeforeExpiry : "-"}</span>
      ),
    },
    {
      title: t("admin.notification.search.recipients"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => (
        <span>{row.recipientCount > 0 ? row.recipientCount : "-"}</span>
      ),
    },
    {
      title: t("admin.notification.search.repeat"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => {
        if (!row.isRepeating) return <span>-</span>;

        const parts = [];
        if (row.repeatIntervalDays) parts.push(`${row.repeatIntervalDays}d`);
        if (row.repeatCount) parts.push(`×${row.repeatCount}`);

        // Oznaka je kratka da stane u redak, pa objašnjenje i primjer plana idu u tooltip —
        // isti plan koji korisnik vidi i pri unosu notifikacije.
        const expiryDate = getExampleExpiryDate();
        const schedule = buildNotificationSchedule(
          expiryDate,
          row.daysBeforeExpiry,
          row.repeatIntervalDays,
          row.repeatCount
        );

        return (
          <Tooltip
            position="top-center"
            size="medium"
            trigger={
              <span className="cursor-help">
                <Badge text={parts.join(" ") || "Da"} variant="info" height="small" showDot />
              </span>
            }
          >
            <div className="min-w-[260px]">
              <div className="text-sm leading-relaxed mb-2">
                {getRepeatDescription(row.repeatIntervalDays, row.repeatCount)}
              </div>

              {schedule.length > 0 && (
                <>
                  <div className="text-xs text-slate-400 mb-1.5">
                    Plan slanja (primjer za istek {formatScheduleDate(expiryDate)}):
                  </div>
                  <div className="flex flex-col gap-1">
                    {schedule.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 text-xs">
                        <span className="text-slate-400 w-20 shrink-0">
                          {formatScheduleDate(item.date)}
                        </span>
                        <span className="text-slate-200">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t("admin.notification.search.missingDate"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => (
        row.notifyIfDateMissing
          ? <Badge text="Da" variant="warning" height="small" showDot />
          : <span>-</span>
      ),
    },
    {
      title: t("admin.notification.search.type"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => (
        <>
          {row.isCustom ? t("admin.notification.custom") : t("admin.notification.automatic")}
        </>
      ),
    },

    {
      title: t("admin.notification.search.created"),
      field: "",
      type: ColumnTypes.custom,
      render: (row) => {
        return (
          <div className="text-md">
            {transformDateToLocalDateString(row.created)}
          </div>
        );
      },
    },
    {
      title: t("admin.notification.search.status"),
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => (
        <Badge
          text={row.isActive ? t("admin.notification.active") : t("admin.notification.inactive")}
          variant={row.isActive ? "success" : "danger"}
          height="small"
          showDot
        />
      ),
    },
    {
      title: "",
      field: "",
      type: ColumnTypes.custom,
      render: (row: NotificationDefinitionRow) => {
        return (
          <ButtonMenu
            endIcon="/media/icons/more-dots.svg"
            cssClassName="h-full"
            variant="text"
          >
            <ButtonMenuItem
              startIcon={row.isActive ? "/media/icons/close.svg" : "/media/icons/check.svg"}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleActive(row.id, row.isActive);
              }}
            >
              {row.isActive ? t("admin.notification.deactivate") : t("admin.notification.activate")}
            </ButtonMenuItem>
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              {t("admin.notification.delete")}
            </ButtonMenuItem>
          </ButtonMenu>
        );
      },
    },
  ];



export const NOTIFICATION_ENTITY_SEARCH_COLUMNS = (
  t: (key: string) => string
): Column[] => [
    {
    title: "Naziv",
    field: "displayName",
    type: ColumnTypes.string
  }
  ];
